import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { CrudStyles } from '../shared/Crud';
import { useCrud } from '../../hooks/useCrud';
import addressTemplateService from '../../services/addressTemplateService';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import { PlusIcon, TrashIcon } from '../Layout/icons';
import ConfirmModal from '../shared/ConfirmModal';
import MessageModal from '../shared/MessageModal';
import AddressTemplateForm from './AddressTemplateForm';
import { AddressPlaceColumnHeader } from '../shared/AddressNameField';
import { addressPlaceLabel } from '../../utils/addressName';

const { EmptyState, Container, Header, Button, TableContainer, PaginationContainer, PageInfo, PageButtons, FiltersContainer, FilterGroup } =
    CrudStyles;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
    th,
    td {
        padding: 14px 16px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
        vertical-align: middle;
    }
    th {
        background: ${({ theme }) => theme?.colors?.background || '#f5f5f5'};
        font-weight: 600;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
        user-select: none;
    }
    th:hover {
        background: ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
    }
    .sort-icon {
        margin-left: 4px;
    }
`;

const ActionCell = styled.td`
    white-space: nowrap;
`;

const ActionBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    margin: 0;
    background: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme?.colors?.danger?.main || '#dc2626'};
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme?.colors?.danger?.main || '#dc2626'};
    &:hover:not(:disabled) {
        background: rgba(220, 38, 38, 0.08);
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
    svg {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }
`;

function formatAddressLabel(item) {
    if (!item) return 'este endereço';
    const place = addressPlaceLabel(item);
    const parts = [
        place === '—' ? null : place,
        item.number,
        item.district,
        item.city,
        item.state?.abbreviation ?? item.stateAbbr,
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : 'este endereço';
}

/**
 * @param {Object} [props]
 * @param {boolean} [props.embedded] - Quando true, renderiza apenas o conteúdo (sem PageWithCustomerSelector).
 */
export default function AddressTemplates({ embedded = false } = {}) {
    const { selectedCustomerId, isTransportCompany, isManagingOwnData } = useCustomerSelector();
    const prevCustomerRef = useRef(selectedCustomerId);

    const canManage = isTransportCompany
        ? Boolean(selectedCustomerId && !isManagingOwnData)
        : Boolean(selectedCustomerId);

    const customerIdForApi = isTransportCompany && selectedCustomerId ? selectedCustomerId : undefined;

    const adaptedService = useMemo(() => {
        if (!canManage) return null;
        return {
            list: (params) =>
                addressTemplateService.list({
                    ...params,
                    customerId: customerIdForApi,
                }),
            get: async () => ({}),
            create: (data) => addressTemplateService.create(data, customerIdForApi),
            update: async () => {},
            delete: async (id) => addressTemplateService.remove(id, customerIdForApi),
        };
    }, [canManage, customerIdForApi]);

    const mockService = useMemo(
        () => ({
            list: async () => ({ content: [], page: { totalPages: 0, number: 0, first: true, last: true } }),
            get: async () => ({}),
            create: async () => {},
            update: async () => {},
            delete: async () => {},
        }),
        []
    );

    const {
        view,
        data,
        loading,
        pagination,
        handlePageChange,
        localFilters,
        handleFilterChange,
        handleCreate,
        handleNew,
        handleCancel,
        setRefetchTrigger,
        setView,
    } = useCrud(adaptedService || mockService, {
        pageSize: 10,
        defaultFilters: { name: '' },
        filterDebounceMs: 400,
    });

    const [pendingRemove, setPendingRemove] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    useEffect(() => {
        if (prevCustomerRef.current !== selectedCustomerId) {
            prevCustomerRef.current = selectedCustomerId;
            setView('list');
            setPendingRemove(null);
            setRefetchTrigger((prev) => prev + 1);
        }
    }, [selectedCustomerId, setView, setRefetchTrigger]);

    useEffect(() => {
        if (!canManage && view === 'create') {
            setView('list');
        }
    }, [canManage, view, setView]);

    const handleConfirmRemove = useCallback(async () => {
        if (!pendingRemove?.id || !adaptedService?.delete) {
            setPendingRemove(null);
            return;
        }
        try {
            setRemovingId(pendingRemove.id);
            await adaptedService.delete(pendingRemove.id);
            setPendingRemove(null);
            setRefetchTrigger((prev) => prev + 1);
        } catch (err) {
            setPendingRemove(null);
            setMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || 'Não foi possível excluir o endereço.',
            });
        } finally {
            setRemovingId(null);
        }
    }, [adaptedService, pendingRemove, setRefetchTrigger]);

    const columnsConfig = [
        { header: <AddressPlaceColumnHeader />, accessor: 'place' },
        { header: 'Número', accessor: 'number' },
        { header: 'Bairro', accessor: 'district' },
        { header: 'Cidade', accessor: 'city' },
        { header: 'UF', accessor: 'state' },
        { header: 'CEP', accessor: 'zipCode' },
        { header: 'Complemento', accessor: 'complement' },
    ];

    const innerContent =
        view === 'create' ? (
            <AddressTemplateForm
                key={`create-${selectedCustomerId}`}
                onSubmit={handleCreate}
                onCancel={handleCancel}
                customerIdForCep={customerIdForApi ?? null}
            />
        ) : !canManage ? (
            <EmptyState>
                <h3>{isTransportCompany ? 'Selecione um cliente' : 'Selecione uma opção'}</h3>
                <p>
                    {isTransportCompany
                        ? 'Para gerenciar endereços recorrentes, selecione um cliente no seletor acima. Não é possível gerenciar endereços da própria empresa.'
                        : 'Selecione uma opção no seletor acima para gerenciar endereços recorrentes.'}
                </p>
            </EmptyState>
        ) : (
            <Container>
                {messageModal.open && (
                    <MessageModal
                        title={messageModal.title}
                        message={messageModal.message}
                        onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
                    />
                )}
                <ConfirmModal
                    open={!!pendingRemove}
                    title="Excluir endereço recorrente"
                    message={
                        pendingRemove
                            ? `Deseja excluir o endereço "${formatAddressLabel(pendingRemove)}"?\n\nEsta ação não pode ser desfeita.`
                            : null
                    }
                    cancelLabel="Cancelar"
                    confirmLabel="Excluir"
                    confirmVariant="danger"
                    onCancel={() => setPendingRemove(null)}
                    onConfirm={handleConfirmRemove}
                />
                <Header>
                    <h1>Endereços recorrentes</h1>
                    <Button variant="primary" onClick={handleNew}>
                        <PlusIcon />
                        Novo
                    </Button>
                </Header>

                <FiltersContainer>
                    <FilterGroup>
                        <label htmlFor="address-template-name-filter">Nome</label>
                        <input
                            id="address-template-name-filter"
                            name="name"
                            value={localFilters.name || ''}
                            onChange={handleFilterChange}
                            placeholder="Buscar por nome..."
                            maxLength={100}
                            autoComplete="off"
                        />
                    </FilterGroup>
                </FiltersContainer>

                <TableContainer>
                    <Table>
                        <thead>
                            <tr>
                                {columnsConfig.map((col) => (
                                    <th key={col.accessor}>
                                        {col.header}
                                    </th>
                                ))}
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '32px' }}>
                                        Carregando...
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: '32px' }}>
                                        Nenhum registro encontrado
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id}>
                                        <td>{addressPlaceLabel(item)}</td>
                                        <td>{item.number ?? '—'}</td>
                                        <td>{item.district ?? '—'}</td>
                                        <td>{item.city ?? '—'}</td>
                                        <td>{item.state?.abbreviation ?? '—'}</td>
                                        <td>{item.zipCode ?? '—'}</td>
                                        <td>{item.complement ?? '—'}</td>
                                        <ActionCell>
                                            <ActionBtn
                                                type="button"
                                                disabled={removingId === item.id}
                                                onClick={() => setPendingRemove(item)}
                                                title="Excluir"
                                            >
                                                <TrashIcon />
                                                {removingId === item.id ? 'Excluindo...' : 'Excluir'}
                                            </ActionBtn>
                                        </ActionCell>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </TableContainer>

                {pagination && (
                    <PaginationContainer>
                        <PageInfo>
                            Página {pagination.totalPages === 0 ? 0 : pagination.page + 1} de{' '}
                            {pagination.totalPages || 0}
                        </PageInfo>
                        <PageButtons>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.first}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                Anterior
                            </Button>
                            <Button
                                variant="outline"
                                disabled={pagination.totalPages === 0 || pagination.last}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                Próxima
                            </Button>
                        </PageButtons>
                    </PaginationContainer>
                )}
            </Container>
        );

    if (embedded) {
        return innerContent;
    }
    return <PageWithCustomerSelector>{innerContent}</PageWithCustomerSelector>;
}
